import { Box, Button, IconButton, Typography, List, ListItem, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import React, { useState, useMemo } from 'react';
import { MultiFilePickerProps, MultiFilePickerLabels } from './multi-file-picker-props';

const DEFAULT_MAX_FILES = 500;
const DEFAULT_ACCEPTED_MIME_TYPES: string[] = [];
const DEFAULT_ACCEPT_ATTRIBUTE = '';

const DEFAULT_LABELS: Required<MultiFilePickerLabels> = {
  dropZoneText: 'Træk filer hertil',
  dropZoneHelperText: 'Maks 500 filer',
  selectFilesButton: 'Vælg filer',
  noFilesMessage: 'Der er ikke valgt nogen fil',
  singleFilePrefix: 'Valgt fil:',
  multipleFilesSuffix: 'filer valgt',
  showListButton: 'Vis liste',
  clearFileTooltip: 'Ryd fil',
  clearFilesTooltip: 'Ryd filer',
  fileListDialogTitle: count => `Valgte filer (${count})`,
  totalSizeLabel: 'Samlet størrelse:',
  closeButton: 'Luk',
  invalidFileTypeError: 'Filtypen er ikke tilladt',
  maxFilesExceededError: (max, count) => `Maksimalt ${max} filer. Du har valgt ${count} filer.`,
  fileTooLargeError: (fileName) => `Filen er for stor: ${fileName}`,
};

function defaultIsDuplicate(fileName1: string, fileName2: string): boolean {
  return fileName1.toLowerCase() === fileName2.toLowerCase();
}

export function MultiFilePicker(props: MultiFilePickerProps) {
  const {
    files,
    onFilesChange,
    error,
    onErrorChange,
    acceptedMimeTypes = DEFAULT_ACCEPTED_MIME_TYPES,
    acceptedExtensions,
    acceptAttribute = DEFAULT_ACCEPT_ATTRIBUTE,
    maxFiles = DEFAULT_MAX_FILES,
    maxFileSizeBytes,
    multiple = true,
    isDuplicate = defaultIsDuplicate,
    disabled = false,
    labels: customLabels,
  } = props;

  const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...customLabels }), [customLabels]);

  const [isDragOver, setIsDragOver] = useState(false);
  const [showFileListDialog, setShowFileListDialog] = useState(false);

  const totalSize = calculateTotalSize(files);

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    processNewFiles(droppedFiles);
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    processNewFiles(Array.from(selectedFiles));
    e.target.value = '';
  }

  function processNewFiles(newFiles: File[]) {
    const validFiles = acceptedExtensions
      ? filterByExtension(newFiles, acceptedExtensions)
      : filterByMimeType(newFiles, acceptedMimeTypes);

    if (validFiles.length === 0) {
      onErrorChange(labels.invalidFileTypeError);
      return;
    }

    const mergedFiles = mergeFiles(files, validFiles, isDuplicate);

    if (mergedFiles.length > maxFiles) {
      onErrorChange(labels.maxFilesExceededError(maxFiles, mergedFiles.length));
      return;
    }

    if (maxFileSizeBytes !== undefined) {
      const oversizedFiles = mergedFiles.filter(f => f.size > maxFileSizeBytes);
      if (oversizedFiles.length > 0) {
        const acceptedFiles = mergedFiles.filter(f => f.size <= maxFileSizeBytes);
        onFilesChange(acceptedFiles);
        onErrorChange(oversizedFiles.map(f => labels.fileTooLargeError(f.name)).join(' '));
        return;
      }
    }

    onFilesChange(mergedFiles);
    onErrorChange('');
  }

  function handleClearFiles() {
    onFilesChange([]);
    onErrorChange('');
  }

  return (
    <>
      <DropZone
        isDragOver={isDragOver}
        disabled={disabled}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        dropZoneText={labels.dropZoneText}
        dropZoneHelperText={labels.dropZoneHelperText}
      />

      <FileInputRow
        files={files}
        totalSize={totalSize}
        multiple={multiple}
        acceptAttribute={acceptAttribute}
        disabled={disabled}
        onFileInputChange={handleFileInputChange}
        onClearFiles={handleClearFiles}
        onShowFileList={() => setShowFileListDialog(true)}
        labels={labels}
      />

      {error && <ErrorMessage message={error} />}

      <FileListDialog
        open={showFileListDialog}
        files={files}
        totalSize={totalSize}
        onClose={() => setShowFileListDialog(false)}
        onRemoveFile={(idx) => {
          const updated = files.filter((_, i) => i !== idx);
          onFilesChange(updated);
          onErrorChange('');
        }}
        title={labels.fileListDialogTitle(files.length)}
        totalSizeLabel={labels.totalSizeLabel}
        closeButtonText={labels.closeButton}
      />
    </>
  );
}

function filterByExtension(files: File[], acceptedExtensions: string[]): File[] {
  if (acceptedExtensions.length === 0) return files;
  const lowerExtensions = acceptedExtensions.map(e => e.toLowerCase());
  return files.filter(f => {
    const dotIndex = f.name.lastIndexOf('.');
    if (dotIndex < 0) return false;
    return lowerExtensions.includes(f.name.substring(dotIndex).toLowerCase());
  });
}

function filterByMimeType(files: File[], acceptedMimeTypes: string[]): File[] {
  if (acceptedMimeTypes.length === 0) return files;
  return files.filter(f => acceptedMimeTypes.includes(f.type));
}

type IsDuplicateFn = (fileName1: string, fileName2: string) => boolean;

function mergeFiles(existingFiles: File[], newFiles: File[], isDuplicate: IsDuplicateFn): File[] {
  const result: File[] = [];

  for (const existingFile of existingFiles) {
    const replacementFile = newFiles.find(newFile => isDuplicate(existingFile.name, newFile.name));
    result.push(replacementFile ?? existingFile);
  }

  for (const newFile of newFiles) {
    const alreadyExists = result.some(file => isDuplicate(file.name, newFile.name));
    if (!alreadyExists) {
      result.push(newFile);
    }
  }

  return sortFilesByName(result);
}

function sortFilesByName(files: File[]): File[] {
  return files.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

function calculateTotalSize(files: File[]): number {
  return files.reduce((sum, file) => sum + file.size, 0);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export interface DropZoneProps {
  isDragOver: boolean;
  disabled: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  dropZoneText: string;
  dropZoneHelperText: string;
}

export function DropZone({ isDragOver, disabled, onDragOver, onDragLeave, onDrop, dropZoneText, dropZoneHelperText }: DropZoneProps) {
  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        border: '2px dashed',
        borderColor: isDragOver ? 'primary.main' : 'grey.400',
        borderRadius: 1,
        p: 3,
        mt: 1,
        textAlign: 'center',
        backgroundColor: isDragOver ? 'action.hover' : 'grey.50',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
        '&:hover': disabled ? {} : { borderColor: 'primary.main', backgroundColor: 'action.hover' },
      }}
    >
      <Typography variant="body1" color="text.secondary">
        {dropZoneText}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {dropZoneHelperText}
      </Typography>
    </Box>
  );
}

interface FileInputRowProps {
  files: File[];
  totalSize: number;
  multiple: boolean;
  acceptAttribute: string;
  disabled: boolean;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFiles: () => void;
  onShowFileList: () => void;
  labels: Required<MultiFilePickerLabels>;
}

function FileInputRow({ files, totalSize, multiple, acceptAttribute, disabled, onFileInputChange, onClearFiles, onShowFileList, labels }: FileInputRowProps) {
  return (
    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <FileInputButton
        onFileInputChange={onFileInputChange}
        buttonText={labels.selectFilesButton}
        multiple={multiple}
        acceptAttribute={acceptAttribute}
        disabled={disabled}
      />

      {files.length === 0 && <NoFilesMessage message={labels.noFilesMessage} />}
      {files.length === 1 && (
        <SingleFileMessage
          file={files[0]}
          onClear={onClearFiles}
          prefix={labels.singleFilePrefix}
          clearTooltip={labels.clearFileTooltip}
          disabled={disabled}
        />
      )}
      {files.length > 1 && (
        <MultipleFilesMessage
          count={files.length}
          totalSize={totalSize}
          onShowList={onShowFileList}
          onClear={onClearFiles}
          suffix={labels.multipleFilesSuffix}
          showListText={labels.showListButton}
          clearTooltip={labels.clearFilesTooltip}
          disabled={disabled}
        />
      )}
    </Box>
  );
}

interface FileInputButtonProps {
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  buttonText: string;
  multiple: boolean;
  acceptAttribute: string;
  disabled: boolean;
}

function FileInputButton({ onFileInputChange, buttonText, multiple, acceptAttribute, disabled }: FileInputButtonProps) {
  return (
    <Button variant="outlined" component="label" size="small" disabled={disabled} sx={{ flexShrink: 0 }}>
      {buttonText}
      <input type="file" accept={acceptAttribute} multiple={multiple} onChange={onFileInputChange} hidden disabled={disabled} />
    </Button>
  );
}

interface NoFilesMessageProps {
  message: string;
}

function NoFilesMessage({ message }: NoFilesMessageProps) {
  return (
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  );
}

interface SingleFileMessageProps {
  file: File;
  onClear: () => void;
  prefix: string;
  clearTooltip: string;
  disabled: boolean;
}

function SingleFileMessage({ file, onClear, prefix, clearTooltip, disabled }: SingleFileMessageProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
      <Typography variant="body2" color="text.secondary" noWrap title={file.name}>
        {prefix} {file.name} ({formatFileSize(file.size)})
      </Typography>
      <ClearButton onClick={onClear} title={clearTooltip} disabled={disabled} />
    </Box>
  );
}

interface MultipleFilesMessageProps {
  count: number;
  totalSize: number;
  onShowList: () => void;
  onClear: () => void;
  suffix: string;
  showListText: string;
  clearTooltip: string;
  disabled: boolean;
}

function MultipleFilesMessage({ count, totalSize, onShowList, onClear, suffix, showListText, clearTooltip, disabled }: MultipleFilesMessageProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {count} {suffix} ({formatFileSize(totalSize)})
      </Typography>
      <Button size="small" variant="text" onClick={onShowList}>
        {showListText}
      </Button>
      <ClearButton onClick={onClear} title={clearTooltip} disabled={disabled} />
    </Box>
  );
}

interface ClearButtonProps {
  onClick: () => void;
  title: string;
  disabled: boolean;
}

function ClearButton({ onClick, title, disabled }: ClearButtonProps) {
  return (
    <IconButton onClick={onClick} size="small" title={title} disabled={disabled}>
      <ClearIcon fontSize="small" />
    </IconButton>
  );
}

interface ErrorMessageProps {
  message: string;
}

function ErrorMessage({ message }: ErrorMessageProps) {
  return (
    <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
      {message}
    </Box>
  );
}

interface FileListDialogProps {
  open: boolean;
  files: File[];
  totalSize: number;
  onClose: () => void;
  onRemoveFile: (index: number) => void;
  title: string;
  totalSizeLabel: string;
  closeButtonText: string;
}

function FileListDialog({ open, files, totalSize, onClose, onRemoveFile, title, totalSizeLabel, closeButtonText }: FileListDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {totalSizeLabel} {formatFileSize(totalSize)}
        </Typography>
        <List dense disablePadding sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
          {files.map((file, idx) => (
            <ListItem
              key={idx}
              sx={{
                py: 0.75,
                pl: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
                '&:hover .remove-file-btn': { visibility: 'visible' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, mr: 2 }}>
                <IconButton
                  className="remove-file-btn"
                  size="small"
                  title="Fjern fil"
                  onClick={() => onRemoveFile(idx)}
                  sx={{ visibility: 'hidden', mr: 0.5, p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography variant="body2" fontWeight={500} noWrap>{file.name}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{formatFileSize(file.size)}</Typography>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {closeButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
